<!-- src/views/bsfp/BsfpAttendanceView.vue -->
<template>
  <section class="page">
    <div class="header">
      <h2 class="page__title">{{ $t('bsfp.titles.attendance') }}</h2>
    </div>

    <div class="controls-panel">
      <!-- Выбор периода -->
      <div class="control-group">
        <label class="control-label">{{ $t('bsfp.period') }}</label>
        <div class="date-range">
          <DatePicker
            v-model="startDate"
            format="iso"
            class="date-input"
            @update:modelValue="loadData"
          />
          <span class="separator">—</span>
          <DatePicker
            v-model="endDate"
            format="iso"
            class="date-input"
            @update:modelValue="loadData"
          />
        </div>
      </div>

      <!-- Выбор группы -->
      <div class="control-group">
        <label class="control-label">{{ $t('bsfp.group') }}</label>
        <select v-model="selectedGroup" class="group-select" @change="loadData">
          <option value="">{{ $t('bsfp.allGroups') }}</option>
          <option v-for="group in groups" :key="group.id" :value="group.id">
            {{ group.name }}
          </option>
        </select>
      </div>

      <!-- Кнопка сохранения -->
      <button @click="saveData" class="btn-save">
        💾 {{ $t('common.save') }}
      </button>
    </div>

    <!-- Таблица посещений -->
    <div class="table-container">
      <div class="table-wrapper">
        <div class="table-header">
          <div class="table-header-left">
            {{ startDate }} — {{ endDate }}
          </div>
          <div class="nav-buttons">
            <button @click="previousWeek" class="btn-nav">{{ $t('bsfp.previousWeek') }}</button>
            <button @click="nextWeek" class="btn-nav">{{ $t('bsfp.nextWeek') }}</button>
          </div>
        </div>

        <table class="attendance-table">
          <thead>
            <tr>
              <th class="col-name">{{ $t('kps.personnel.colFio') }}</th>
              <th v-for="day in days" :key="day" class="col-day">
                <div class="day-header">
                  <div class="day-number">{{ formatDay(day) }}</div>
                  <div class="day-name">{{ getDayShortName(day) }}</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(student, idx) in students" :key="student.id" :class="['student-row', { 'even': idx % 2 === 0 }]">
              <td class="col-name" :data-name="student.full_name">{{ student.full_name }}</td>
              <td 
                v-for="day in days" 
                :key="day" 
                class="col-day"
                @click="openMarksModal(student, day)"
              >
                <div class="cell-content" :class="getCellClass(student.id, day)">
                  {{ getCellValue(student.id, day) || '—' }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Легенда -->
    <div class="legend">
      <div class="legend-item">
        <span class="legend-label">Н</span>
        <span>{{ $t('bsfp.legend.n') }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-label">Б</span>
        <span>{{ $t('bsfp.legend.b') }}</span>
      </div>
      <div class="legend-item">
        <span class="legend-label">1-5</span>
        <span>{{ $t('bsfp.legend.mark') }}</span>
      </div>
    </div>

    <!-- Модал для отметок -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeMarksModal">
      <div class="modal">
        <h3 class="modal-title">
          {{ selectedStudent?.full_name }} — {{ formatDate(selectedDate) }}
        </h3>

        <div class="modal-buttons">
          <button
            v-for="mark in markOptions"
            :key="mark.value"
            @click="setMark(mark.value)"
            :class="['mark-btn', { active: currentMark === mark.value }]"
          >
            {{ mark.label }}
          </button>
        </div>

        <div class="modal-actions">
          <button @click="closeMarksModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
          <button @click="saveMark" class="btn-confirm">{{ $t('common.save') }}</button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">
          {{ toastMessage }}
        </div>
      </div>
    </transition>

  </section>
</template>

<script>
import { http } from '@/api/http'
import DatePicker from '@/components/widgets/DatePicker.vue'

export default {
  name: 'BsfpAttendanceView',
  components: { DatePicker },

  data() {
    return {
      startDate: '',
      endDate: '',
      isWeekShifting: false,
      selectedGroup: '',
      groups: [],
      students: [],
      days: [],
      attendance: {},
      showModal: false,
      selectedStudent: null,
      selectedDate: '',
      selectedMark: '',
      toastMessage: '',
      showToast: false
    }
  },
  computed: {
    markOptions() {
      return [
        { value: 'Н', label: this.$t('bsfp.marks.n') },
        { value: 'Б', label: this.$t('bsfp.marks.b') },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '', label: this.$t('bsfp.marks.clear') },
      ]
    },
  },

  mounted() {
    this.initDateRange()
    this.loadGroups()
  },

  methods: {
    shiftWeek(deltaWeeks) {
      if (!this.startDate || !this.endDate) return
      const deltaDays = Number(deltaWeeks) * 7

      const start = new Date(this.startDate + 'T00:00:00')
      const end = new Date(this.endDate + 'T00:00:00')
      start.setDate(start.getDate() + deltaDays)
      end.setDate(end.getDate() + deltaDays)

      this.isWeekShifting = true
      this.startDate = this.formatDateForInput(start)
      this.endDate = this.formatDateForInput(end)

      this.$nextTick(() => {
        this.isWeekShifting = false
        this.generateDays()
        this.loadData()
      })
    },

    previousWeek() {
      this.shiftWeek(-1)
    },

    nextWeek() {
      this.shiftWeek(1)
    },

    initDateRange() {
      const today = new Date()
      const monday = new Date(today)
      monday.setDate(today.getDate() - today.getDay() + 1)
      
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      
      this.startDate = this.formatDateForInput(monday)
      this.endDate = this.formatDateForInput(sunday)
      this.generateDays()
    },

    generateDays() {
      this.days = []
      const start = new Date(this.startDate)
      const end = new Date(this.endDate)
      
      while (start <= end) {
        this.days.push(this.formatDateForInput(new Date(start)))
        start.setDate(start.getDate() + 1)
      }
    },

    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatDay(dateString) {
      const date = new Date(dateString + 'T00:00:00')
      return String(date.getDate()).padStart(2, '0')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr + 'T00:00:00')
      return date.toLocaleDateString('ru-RU', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    getDayShortName(dateString) {
      const date = new Date(dateString + 'T00:00:00')
      const days = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
      return days[date.getDay()]
    },

    async loadGroups() {
      try {
        const { data } = await http.get('attendance/groups/', { params: { district: 'bsfp' } })
        this.groups = Array.isArray(data) ? data : []
        if (this.groups.length > 0) {
          this.selectedGroup = this.groups[0].id
          await this.loadData()
        }
      } catch (error) {
        console.error('Load groups error:', error)
        this.groups = []
      }
    },

    async loadData() {
      try {
        if (!this.selectedGroup) return

        // Загрузи группу с студентами
        const groupResp = await http.get(`attendance/groups/${this.selectedGroup}/`)
        this.students = groupResp?.data?.students || []

        // Загрузи записи о посещаемости
        const { data } = await http.get('attendance/records/', {
          params: {
            group_id: this.selectedGroup,
            start_date: this.startDate,
            end_date: this.endDate,
          },
        })

        // Преобразуй ответ в формат attendance
        this.attendance = {}
        ;(Array.isArray(data) ? data : []).forEach(record => {
          if (!this.attendance[record.student]) {
            this.attendance[record.student] = {}
          }
          this.attendance[record.student][record.date] = record.mark
        })
      } catch (error) {
        console.error('Load error:', error)
        this.students = []
        this.attendance = {}
      }
    },

    getCellValue(studentId, day) {
      return this.attendance[studentId]?.[day] || ''
    },

    getCellClass(studentId, day) {
      const value = this.getCellValue(studentId, day)
      if (value === 'Н') return 'mark-н'
      if (value === 'Б') return 'mark-б'
      if (value === '1') return 'mark-1'
      if (value === '2') return 'mark-2'
      if (value === '3') return 'mark-3'
      if (value === '4') return 'mark-4'
      if (value === '5') return 'mark-5'
      return 'mark-empty'
    },

    openMarksModal(student, day) {
      this.selectedStudent = student
      this.selectedDate = day
      this.selectedMark = this.getCellValue(student.id, day)
      this.showModal = true
    },

    closeMarksModal() {
      this.showModal = false
      this.selectedStudent = null
      this.selectedDate = ''
      this.selectedMark = ''
    },

    setMark(value) {
      this.selectedMark = value
    },

    saveMark() {
      if (!this.attendance[this.selectedStudent.id]) {
        this.attendance[this.selectedStudent.id] = {}
      }
      this.attendance[this.selectedStudent.id][this.selectedDate] = this.selectedMark
      this.closeMarksModal()
    },

    async saveData() {
      try {
        const payload = {
          group_id: this.selectedGroup,
          start_date: this.startDate,
          end_date: this.endDate,
          attendance_data: this.attendance
        }
        
        const { data } = await http.post('attendance/records/bulk_save/', payload)
        
        this.showSuccessToast(`✓ ${data?.message || this.$t('bsfp.toast.saved').replace('✓ ', '')}`)
      } catch (error) {
        console.error('Save error:', error)
        this.showErrorToast(this.$t('bsfp.toast.saveDataError'))
      }
    },

    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },

    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3500)
    }
  },

  watch: {
    selectedGroup() {
      this.loadData()
    },
    startDate() {
      if (this.isWeekShifting) return
      this.generateDays()
      this.loadData()
    },
    endDate() {
      if (this.isWeekShifting) return
      this.generateDays()
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page {
  padding: 2rem;
  background: #f5f7fa;
  min-height: 100vh;
}

.header {
  margin-bottom: 2rem;
}

.page__title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.controls-panel {
  display: flex;
  gap: 2rem;
  align-items: flex-end;
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.date-range {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.date-input {
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
}

.date-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.separator {
  color: #cbd5e1;
  font-weight: 600;
}

.group-select {
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
  width: 200px;
  cursor: pointer;
}

.group-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-save {
  padding: 0.625rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-save:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.table-container {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.table-wrapper {
  overflow-x: auto;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 1rem 1.5rem;
  border-bottom: 2px solid #cbd5e1;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
}

.nav-buttons {
  display: flex;
  gap: 0.75rem;
}

.btn-nav {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
  white-space: nowrap;
}

.btn-nav:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.attendance-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.attendance-table thead {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.attendance-table th {
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.75rem;
  color: #64748b;
  border-right: 1px solid #e2e8f0;
}

.attendance-table th:last-child {
  border-right: none;
}

.attendance-table td {
  padding: 0;
  border-right: 1px solid #e2e8f0;
}

.attendance-table td:last-child {
  border-right: none;
}

.attendance-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.attendance-table tbody tr.even {
  background: white;
}

.attendance-table tbody tr:not(.even) {
  background: #f9fafb;
}

.attendance-table tbody tr:hover {
  background: #eff6ff !important;
}

/* ФИО Column */
.col-name {
  width: 110px;
  flex-shrink: 0;
  padding: 0.75rem 0.5rem !important;
  font-size: 0.85rem;
  color: #1e293b;
  text-align: left;
  font-weight: 600;
  background: #f8fafc;
  border-right: 2px solid #e2e8f0 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Day Columns */
.col-day {
  width: 65px;
  flex-shrink: 0;
  padding: 0 !important;
  text-align: center;
}

.day-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
}

.day-number {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.75rem;
}

.day-name {
  font-size: 0.65rem;
  color: #94a3b8;
  font-weight: 500;
}

.cell-content {
  height: 100%;
  padding: 0.75rem 0.5rem;
  text-align: center;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 700;
  color: #475569;
  transition: all 0.2s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  position: relative;
}

.cell-content:hover {
  background: #dbeafe;
  color: #1e40af;
  font-size: 1rem;
}

/* Mark Colors */
.cell-content.mark-н {
  color: #dc2626;
  font-weight: 700;
}

.cell-content.mark-б {
  color: #ea580c;
  font-weight: 700;
}

.cell-content.mark-1 {
  color: #7c3aed;
}

.cell-content.mark-2 {
  color: #0891b2;
}

.cell-content.mark-3 {
  color: #16a34a;
}

.cell-content.mark-4 {
  color: #d97706;
}

.cell-content.mark-5 {
  color: #059669;
  font-weight: 700;
}

.cell-content.mark-empty {
  color: #cbd5e1;
}

/* Legend */
.legend {
  display: flex;
  gap: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-top: 1rem;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #475569;
}

.legend-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  font-weight: 700;
  color: #64748b;
  font-size: 0.75rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  text-align: center;
}

.modal-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.mark-btn {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #475569;
}

.mark-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

.mark-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
}

.btn-cancel:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-confirm {
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-confirm:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

@media (max-width: 1024px) {
  .controls-panel {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .group-select {
    width: 100%;
  }
  
  .modal-buttons {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }
  
  .controls-panel {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .date-range {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .date-input {
    width: 100%;
  }
  
  .attendance-table {
    font-size: 0.75rem;
  }
  
  .col-name {
    width: 90px;
    padding: 0.5rem 0.25rem !important;
    font-size: 0.8rem;
  }
  
  .col-day {
    width: 55px;
  }
  
  .day-header {
    padding: 0.5rem 0.25rem;
  }
  
  .cell-content {
    padding: 0.5rem 0.25rem;
    min-height: 45px;
  }
  
  .modal {
    width: 95%;
    padding: 1.5rem;
  }
  
  .modal-buttons {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .legend {
    flex-direction: column;
    gap: 1rem;
  }
}
/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toast-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-left: 4px solid #047857;
}

.toast-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-left: 4px solid #b91c1c;
}

.toast-content {
  flex: 1;
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from {
  transform: translateX(400px);
  opacity: 0;
}

.toast-slide-leave-to {
  transform: translateX(400px);
  opacity: 0;
}

@media (max-width: 768px) {
  .toast {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
}

</style>